/**
 * Регистрация Service Worker для PWA
 */

const APP_PATH = '/app/';
const SW_URL = `${APP_PATH}sw.js`;

export const registerServiceWorker = (): void => {
	if (!('serviceWorker' in navigator && typeof window !== 'undefined')) {
		return;
	}
	if (window.location.href.indexOf("127.0.0.1") > -1 ||
		window.location.href.indexOf("localhost:") > -1
	) return;

	window.addEventListener('load', () => {
		navigator.serviceWorker
			.register(SW_URL, {scope: APP_PATH})
			.then((registration: ServiceWorkerRegistration) => {
				console.log('Service Worker зарегистрирован для:', APP_PATH, registration);

				// Проверяем обновления каждые 1 час
				setInterval(() => {
					registration.update();
				}, 60 * 60 * 1000);

				// Отслеживаем обновления Service Worker
				registration.addEventListener('updatefound', () => {
					const newWorker = registration.installing;
					if (newWorker) {
						newWorker.addEventListener('statechange', () => {
							if (newWorker.state === 'installed' && navigator.serviceWorker.controller) {
								console.log('Доступно новое обновление приложения!');
								// Можно показать уведомление пользователю
								if ('Notification' in window && Notification.permission === 'granted') {
									new Notification('Ситаск задачи', {
										body: 'Доступно обновление приложения. Перезагрузите для применения.',
										icon: `${APP_PATH}icons/icon-192x192.png`
									});
								}
							}
						});
					}
				});
			})
			.catch((error: Error) => {
				console.warn('Ошибка регистрации Service Worker для', APP_PATH, ':', error);
			});
	});
};

export const requestNotificationPermission = async (): Promise<boolean> => {
	if (typeof window === 'undefined' || !('Notification' in window)) {
		console.log('Браузер не поддерживает уведомления');
		return false;
	}

	if (Notification.permission === 'granted') {
		return true;
	}

	if (Notification.permission !== 'denied') {
		const permission = await Notification.requestPermission();
		return permission === 'granted';
	}

	return false;
};

export interface BeforeInstallPromptEvent extends Event {
	readonly platforms: string[];
	readonly userChoice: Promise<{
		outcome: 'accepted' | 'dismissed';
		platform: string;
	}>;

	prompt(): Promise<void>;
}

declare global {
	interface WindowEventMap {
		beforeinstallprompt: BeforeInstallPromptEvent;
	}
}

export const showInstallPrompt = (): { show: () => Promise<boolean> } => {
	let deferredPrompt: BeforeInstallPromptEvent | null = null;

	if (typeof window !== 'undefined') {
		window.addEventListener('beforeinstallprompt', (e: Event) => {
			e.preventDefault();
			deferredPrompt = e as BeforeInstallPromptEvent;
		});
	}

	return {
		show: async (): Promise<boolean> => {
			if (!deferredPrompt) return false;

			try {
				await deferredPrompt.prompt();
				const choiceResult = await deferredPrompt.userChoice;
				deferredPrompt = null;
				return choiceResult.outcome === 'accepted';
			} catch (error) {
				console.error('Ошибка показа установки:', error);
				return false;
			}
		}
	};
};

export const isPWAInstalled = (): boolean => {
	if (typeof window === 'undefined') return false;

	return window.matchMedia('(display-mode: standalone)').matches ||
		(window.navigator as any).standalone === true ||
		document.referrer.includes('android-app://');
};

export interface InstallInfo {
	isInstalled: boolean;
	isOnline: boolean;
	supportsPWA: boolean;
	isIOS: boolean;
	appPath: string;
	supportsServiceWorker: boolean;
}

export const getInstallInfo = (): InstallInfo => {
	if (typeof window === 'undefined') {
		return {
			isInstalled: false,
			isOnline: false,
			supportsPWA: false,
			isIOS: false,
			appPath: APP_PATH,
			supportsServiceWorker: false
		};
	}

	const isInstalled = isPWAInstalled();
	const isOnline = navigator.onLine;
	const supportsServiceWorker = 'serviceWorker' in navigator;
	const supportsPWA = supportsServiceWorker && 'BeforeInstallPromptEvent' in window;
	const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;

	return {
		isInstalled,
		isOnline,
		supportsPWA,
		isIOS,
		appPath: APP_PATH,
		supportsServiceWorker
	};
};

export const checkServiceWorkerSupport = (): boolean => {
	return typeof window !== 'undefined' && 'serviceWorker' in navigator;
};

export const getServiceWorkerRegistration = async (): Promise<ServiceWorkerRegistration | null | undefined> => {
	if (!checkServiceWorkerSupport()) return null;

	try {
		return await navigator.serviceWorker.getRegistration(APP_PATH);
	} catch (error) {
		console.error('Ошибка получения регистрации Service Worker:', error);
		return null;
	}
};

/**
 * Отправляет сообщение в Service Worker
 */
export const sendMessageToSW = async (message: any): Promise<void> => {
	if (!checkServiceWorkerSupport()) return;

	try {
		const registration = await navigator.serviceWorker.ready;
		if (registration.active) {
			registration.active.postMessage(message);
		}
	} catch (error) {
		console.error('Ошибка отправки сообщения в Service Worker:', error);
	}
};

/**
 * Проверяет, активен ли Service Worker
 */
export const isServiceWorkerActive = async (): Promise<boolean> => {
	if (!checkServiceWorkerSupport()) return false;

	try {
		const registration = await navigator.serviceWorker.ready;
		return !!registration.active;
	} catch (error) {
		console.error('Ошибка проверки Service Worker:', error);
		return false;
	}
};

